import React, { useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './sheet';
import { Button } from './button';
import { ScrollArea } from './scroll-area';
import { Separator } from './separator';
import { HelpCircle, Lightbulb, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable HelpPanel component for contextual help across the app.
 * Opens as a right-side sheet with structured help content.
 * 
 * Usage:
 * <HelpPanel
 *   title="Bank Reconciliation"
 *   description="Learn how to reconcile your bank accounts"
 *   sections={[
 *     { heading: "What is Cleared?", content: "..." },
 *     { heading: "What is Outstanding?", content: "...", type: "info" },
 *   ]}
 *   tips={["Tip 1", "Tip 2"]}
 *   warnings={["Warning 1"]}
 * />
 */

export function HelpPanel({
  title,
  description,
  sections = [],
  tips = [],
  warnings = [],
  trigger,
  triggerVariant = "ghost",
  triggerSize = "icon",
  side = "right",
  className,
  children,
  tourId,
}) {
  const trackHelpOpened = useCallback((open) => {
    if (open) {
      try {
        const wsId = localStorage.getItem('currentWorkspaceId') || '';
        const token = localStorage.getItem('cb_session_token') || '';
        if (wsId && token && API_URL) {
          fetch(`${API_URL}/api/workspaces/${wsId}/onboarding/help-opened`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          }).catch(() => {});
        }
      } catch {}
    }
  }, []);

  return (
    <Sheet onOpenChange={trackHelpOpened}>
      <SheetTrigger asChild>
        {trigger || (
          <Button
            variant={triggerVariant}
            size={triggerSize}
            className={cn(
              "relative text-primary/70 hover:text-primary hover:bg-primary/10 border border-primary/20 hover:border-primary/40 transition-all",
              triggerSize === "icon" ? "h-9 w-9 rounded-full" : "",
              className
            )}
            data-testid="help-panel-trigger"
          >
            <HelpCircle className="w-[18px] h-[18px]" />
            <span className="sr-only">Help</span>
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side={side} className="w-[400px] sm:w-[450px]" data-testid="help-panel-content">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            {title}
          </SheetTitle>
          {description && (
            <SheetDescription>{description}</SheetDescription>
          )}
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-120px)] mt-6 pr-4">
          <div className="space-y-6">
            {/* Take a Tour button */}
            {tourId && (
              <a
                href={`/guided-tours?start=${tourId}`}
                className="flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors group"
                data-testid="take-a-tour-btn"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <svg className="w-4 h-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary">Take a Guided Tour</p>
                  <p className="text-[11px] text-muted-foreground">Interactive walkthrough of this page</p>
                </div>
              </a>
            )}
            {/* Main Sections */}
            {sections.map((section, index) => (
              <div key={index} className="space-y-2">
                <h3 className={cn(
                  "font-semibold text-sm",
                  section.type === "info" && "text-blue-600 dark:text-blue-400",
                  section.type === "success" && "text-emerald-600 dark:text-emerald-400",
                  section.type === "warning" && "text-amber-600 dark:text-amber-400"
                )}>
                  {section.heading}
                </h3>
                <div className="text-sm text-muted-foreground leading-relaxed">
                  {typeof section.content === 'string' ? (
                    <p>{section.content}</p>
                  ) : (
                    section.content
                  )}
                </div>
                {index < sections.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}

            {/* Tips Section */}
            {tips.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                    <Lightbulb className="w-4 h-4" />
                    Tips
                  </h3>
                  <ul className="space-y-2">
                    {tips.map((tip, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {/* Warnings Section */}
            {warnings.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    Important
                  </h3>
                  <ul className="space-y-2">
                    {warnings.map((warning, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {/* Custom Children Content */}
            {children && (
              <>
                <Separator />
                {children}
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Guided callout component for first-time user tours.
 * Appears as a floating tooltip pointing to a specific element.
 */
export function GuidedCallout({
  children,
  title,
  description,
  position = "bottom", // top, bottom, left, right
  show = true,
  onDismiss,
  step,
  totalSteps,
}) {
  if (!show) return children;

  const positionClasses = {
    top: "bottom-full mb-2 left-1/2 -translate-x-1/2",
    bottom: "top-full mt-2 left-1/2 -translate-x-1/2",
    left: "right-full mr-2 top-1/2 -translate-y-1/2",
    right: "left-full ml-2 top-1/2 -translate-y-1/2",
  };

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-primary",
    bottom: "bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-primary",
    left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-primary",
    right: "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-primary",
  };

  return (
    <div className="relative inline-block">
      {children}
      <div
        className={cn(
          "absolute z-50 w-64 p-3 rounded-lg bg-primary text-primary-foreground shadow-lg animate-in fade-in-0 zoom-in-95",
          positionClasses[position]
        )}
        data-testid="guided-callout"
      >
        {/* Arrow */}
        <div
          className={cn(
            "absolute w-0 h-0 border-8",
            arrowClasses[position]
          )}
        />
        
        {/* Content */}
        <div className="space-y-2">
          {title && <p className="font-semibold text-sm">{title}</p>}
          {description && <p className="text-xs opacity-90">{description}</p>}
          
          <div className="flex items-center justify-between pt-1">
            {step && totalSteps && (
              <span className="text-xs opacity-70">
                {step} of {totalSteps}
              </span>
            )}
            <Button
              size="sm"
              variant="secondary"
              className="h-7 text-xs"
              onClick={onDismiss}
            >
              Got it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Simple hover tooltip for inline help on labels.
 */
export function HelpTooltip({ children, content }) {
  return (
    <span className="relative group cursor-help">
      {children}
      <span className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded bg-popover text-popover-foreground border shadow-md whitespace-nowrap max-w-[200px]">
        {content}
      </span>
    </span>
  );
}

export default HelpPanel;
